import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { Card } from "reactstrap";

import { ReactComponent as BackIcon } from "assets/img/svg/BackArrow.svg";
import AAutoComplete from "views/pages/manage/common/form-fields-mui/AAutoComplete";
import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import ALoader from "views/pages/manage/common/a-loader";

import {
  successToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";

import {
  fetchCommissionSlabDropdowns,
  fetchSlabDetails,
  paymentSlabStatusOptions,
  updateSlabDetails, // <-- single unified API
} from "./helper";

import CommissionSlabTable from "./CommissionSlabTable";
import { useGetPermissions } from "layouts/customHook";
import { PermisionDenied } from "views/pages/manage/common/commonComponents";

const ViewEditCommissionSlab = () => {
  const { id } = useParams();
  const history = useHistory();
  const tableRef = useRef(null);
   const userPermissions = useGetPermissions('commissionSlab');

  // ================= Core State =================
  const [slab, setSlab] = useState(null);
  const [rows, setRows] = useState([]);
  const [dropdowns, setDropdowns] = useState({});

  const [formState, setFormState] = useState({
    status: "",
    extensionBusinessUnit: "",
  });

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ================= Load Slab =================
  const loadSlabDetails = useCallback(async ({ size = pageSize, offset = currentPage } = {}) => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchSlabDetails(id, {offset: offset, pageSize: size});

      if (!data) {
        setError("Slab not found");
        return;
      }

      const mappedRows = (data?.slabs?.content || []).map((row) => ({
        id: row.id,
        effectiveSequence: row.id,
        studentsFrom: row.countFrom,
        studentsTo: row.countTo,
        career: row.career,
        amount: row.amount,
        ...row,
      }));

      setSlab({
        id: data.id,
        businessUnit: data.businessUnit,
        extensionBusinessUnit: data.extensionBusinessUnit,
        year: data.year,
        sapPlantId: data.sapPlantId,
        status: data.status || "",
      });

      setFormState({
        id: data.id,
        year: data.year,
        businessUnit: data.businessUnit,
        status: data.status || "",
        extensionBusinessUnit: data.extensionBusinessUnit || "",
      });
      setTotalCount(data.slabs?.totalElements || 0);
      setPageSize(data?.slabs?.pageSize);
      setCurrentPage(data?.slabs?.pageNumber);
      setRows(mappedRows);
    } catch (err) {
      console.error("Error fetching slab:", err);
      setError(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load slab"
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  // ================= Load Dropdowns =================
  const loadDropdowns = useCallback(async () => {
    try {
      const res = await fetchCommissionSlabDropdowns();
      setDropdowns(res || {});
    } catch (err) {
      console.error("Dropdown fetch failed:", err);
    }
  }, []);

  useEffect(() => {
    loadSlabDetails();
    loadDropdowns();
  }, [loadSlabDetails, loadDropdowns]);

  const handleFieldChange = useCallback((field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleSave = async () => {
    if (!slab) return;

    const hasChanges =
      formState.status !== slab.status ||
      formState.extensionBusinessUnit !== slab.extensionBusinessUnit;

    if (!hasChanges) {
      setEditMode(false);
      return;
    }

    setSaving(true);

    try {
      const res = await updateSlabDetails(id, formState);
      if (res?.code == 200) {
        await loadSlabDetails();
        successToast(res?.message || "Slab updated successfully");
      }
    } catch (err) {
      console.error("Update failed:", err);
    } finally {
      setSaving(false);
      setEditMode(false);
    }
  };

  const handlePaginationChange = useCallback(
    ({ page, pageSize }) => {
      loadSlabDetails({ size: pageSize, offset: page });
    },
    []
  );


  const chips = useMemo(
    () => [
      { label: "BU", value: slab?.businessUnit },
      { label: "Year", value: slab?.year },
    ],
    [slab]
  );

  const statusOption = useMemo(
    () =>
      paymentSlabStatusOptions.find(
        (s) => s.value === formState.status
      ) || null,
    [formState.status]
  );

  const extensionOption = useMemo(
    () =>
      dropdowns?.extensionBusinessUnits?.find(
        (e) => e.value === formState.extensionBusinessUnit
      ) || null,
    [dropdowns, formState.extensionBusinessUnit]
  );


  if (loading) {
    return (
      <div className="p-4 text-center">
        <ALoader />
      </div>
    );
  }

  if (error || !slab) {
    return (
      <div className="p-4">
        <BackIcon
          style={{ cursor: "pointer" }}
          onClick={() => history.goBack()}
        />
        <Card className="p-5 text-center mt-3">
          <h4 className="text-muted">Slab Not Found</h4>
          <p>{error}</p>
          <AButton onClick={() => history.goBack()}>
            Go Back
          </AButton>
        </Card>
      </div>
    );
  }


  return (
     userPermissions ? (
    <div className="p-4">
      {/* Header */}
      <div className="d-flex align-items-center mb-3">
        <BackIcon
          style={{ cursor: "pointer" }}
          onClick={() => history.goBack()}
        />
        <h3 className="heading-3 mb-0 ml-2">Slab Setup</h3>

        <div className="ml-auto d-flex gap-2">
          {chips.map((chip) => (
            <span key={chip.label} className="chip">
              {chip.label}: <strong>{chip.value}</strong>
            </span>
          ))}
        </div>
      </div>

      {/* Info Card */}
      <Card className="p-4 mb-4">
        <div className="d-flex flex-wrap" style={{ gap: '3rem' }}>

          <EditableField
            label="Extension Business Area"
            editMode={editMode}
            value={slab.extensionBusinessUnit}
            component={
              <AAutoComplete
                items={dropdowns?.extensionBusinessUnits || []}
                currentValue={extensionOption}
                handleChange={(opt) =>
                  handleFieldChange("extensionBusinessUnit", opt?.value || "")
                }
                disabled={saving}
                height="36px"
                isMulti={false}
              />
            }
          />
          {/* Status */}
          <EditableField
            label="Status"
            editMode={editMode}
            value={slab.status}
            component={
              <AAutoComplete
                items={paymentSlabStatusOptions}
                currentValue={statusOption}
                handleChange={(opt) =>
                  handleFieldChange("status", opt?.value || "")
                }
                disabled={saving}
                height="36px"
                isMulti={false}
              />
            }
          />
          <InfoField label="SAP Plant ID" value={slab.sapPlantId} />
          {editMode ? (
            <AButton onClick={handleSave} size="xs" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </AButton>
          ) : (
            <AButton variant="outline" size="xs" onClick={() => setEditMode(true)} disabled={!(userPermissions||[]).includes("U")}>
              Edit
            </AButton>
          )}
        </div>

        <div className="mt-3">

        </div>
      </Card>

      {/* Table */}
      <Card className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="text-bold mb-0">Commission Slabs</h4>
          <AButton size="xs" onClick={() => tableRef.current?.addRow()} disabled={!(userPermissions||[]).includes("C")}>
            Add Row
          </AButton>
        </div>

        <CommissionSlabTable
          ref={tableRef}
          rows={rows}
          setRows={setRows}
          currPageSize={pageSize}
          currentPage={currentPage}
          totalRecords={totalCount}
          onPaginationChange={handlePaginationChange}
          slabId={id}
          userPermissions={userPermissions}
          loadSlabDetails={loadSlabDetails}
          {...dropdowns}
        />
      </Card>
    </div>
     ) : (
          <PermisionDenied/>
        )
  );
};


const InfoField = ({ label, value }) => (
  <div style={{ minWidth: 200 }}>
    <div className="text-muted">{label}</div>
    <strong>{value || "-"}</strong>
  </div>
);

const EditableField = ({ label, value, editMode, component }) => (
  <div style={{ minWidth: 200 }}>
    <div className="text-muted">{label}</div>
    {editMode ? <div style={{ minWidth: 200 }}>{component}</div> : <strong>{value || "-"}</strong>}
  </div>
);

export default ViewEditCommissionSlab;
